import { el, mount } from './dom';
import { t } from '@core/i18n';
import { getGame } from '@core/game-registry';
import { createPlayer, createTeam, defaultTurnOrder, validateConfig } from '@core/players';
import { navigate } from './router';
import { assetUrl } from './assets';
import type { GameConfig, GameMode, Player, Team, TurnEntity } from '@core/types';
import { saveSession } from '@core/session';
import { TurnManager } from '@core/turn-manager';
import { copy } from '@games/arcade/catalog';

const TEAM_COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#ec4899'];

export function renderSetup(gameId: string): HTMLElement {
  const game = getGame(gameId);
  if (!game) return el('div', { class: 'p-6' }, [`Unknown game: ${gameId}`]);

  let mode: GameMode = 'single';
  let players: Player[] = [createPlayer(t('common.you'))];
  let teams: Team[] = [];
  let turnOrder: TurnEntity[] = defaultTurnOrder({ mode, players, teams });
  let daily = false; // default casuale; l'utente può attivare la modalità daily dal toggle
  let error = '';
  let starting = false;
  let disposed = false;

  const root = el('main', { class: 'mx-auto max-w-2xl p-4 sm:p-6', dataset: { theme: gameId } });

  function rebuildTurnOrder(): void {
    turnOrder = defaultTurnOrder({ mode, players, teams });
  }

  function moveOrder(idx: number, dir: -1 | 1): void {
    const j = idx + dir;
    if (j < 0 || j >= turnOrder.length) return;
    [turnOrder[idx], turnOrder[j]] = [turnOrder[j], turnOrder[idx]];
    render();
  }

  function entityLabel(e: TurnEntity): string {
    if (e.kind === 'team') return teams.find((t) => t.id === e.id)?.name ?? '?';
    return players.find((p) => p.id === e.id)?.name ?? '?';
  }

  function teamSelect(player: Player): HTMLSelectElement {
    const select = el('select', {
      class: 'input max-w-[10rem]',
      on: { change: (event) => { player.teamId = (event.target as HTMLSelectElement).value; } },
    }, teams.map((tt) => el('option', { value: tt.id }, [tt.name])));
    select.value = player.teamId ?? '';
    select.setAttribute('aria-label', copy(`Squadra di ${player.name}`, `Team for ${player.name}`));
    return select;
  }

  async function start(): Promise<void> {
    if (starting || disposed) return;
    const config: GameConfig = {
      mode,
      players: players.map((p) => ({ ...p })),
      teams: teams.map((tt) => ({ ...tt })),
      turnOrder: turnOrder.map((entity) => ({ ...entity })),
      daily,
    };
    const v = validateConfig(config, game!.metadata.minPlayers, game!.metadata.maxPlayers);
    if (!v.ok) {
      const code = v.error ?? '';
      if (code.startsWith('min_players:')) {
        error = t('setup.error.min_players', { n: code.split(':')[1] });
      } else if (code === 'empty_name') {
        error = t('setup.error.empty_name');
      } else if (code.startsWith('max_players:')) {
        error = copy(`Massimo ${game!.metadata.maxPlayers} giocatori.`, `Maximum ${game!.metadata.maxPlayers} players.`);
      } else if (code === 'empty_team') {
        error = copy('Ogni squadra deve avere almeno un giocatore.', 'Each team needs at least one player.');
      } else if (code === 'empty_team_name') {
        error = copy('Dai un nome a ogni squadra.', 'Give every team a name.');
      } else if (code === 'unassigned_player') {
        error = copy('Assegna ogni giocatore a una squadra.', 'Assign every player to a team.');
      } else {
        error = code;
      }
      render();
      return;
    }
    starting = true; error = ''; render();
    try {
      await game!.loadAssets();
      if (disposed) return;
      const sessionId = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
      const tm = new TurnManager({ gameType: game!.id, gameId: sessionId, config, module: game! });
      await saveSession(tm.serialize());
      if (!disposed) navigate({ name: 'play', gameId: game!.id, sessionId });
    } catch (e) {
      console.error(e);
      error = copy('Caricamento non riuscito. Controlla la connessione e riprova.', 'Loading failed. Check your connection and try again.');
    } finally { starting = false; if (!disposed) render(); }
  }

  function render(): void {
    const modeButtons = (['single', 'multi-players', 'teams'] as GameMode[])
      .filter((m) => m !== 'teams' || game!.metadata.supportsTeams)
      .map((m) =>
        el(
          'button',
          {
            class: `btn ${mode === m ? 'btn-primary' : 'btn-ghost'}`,
            on: {
              click: () => {
                mode = m;
                if (m === 'single') players = players.slice(0, 1);
                if (m === 'teams' && teams.length === 0) {
                  teams = [
                    createTeam('Team A', TEAM_COLORS[0]),
                    createTeam('Team B', TEAM_COLORS[1]),
                  ];
                }
                if (m === 'teams') {
                  while (players.length < teams.length) players.push(createPlayer(`P${players.length + 1}`));
                  players.forEach((p, idx) => { if (!teams.some((tt) => tt.id === p.teamId)) p.teamId = teams[idx % teams.length].id; });
                  for (const empty of teams.filter((tt) => !players.some((p) => p.teamId === tt.id))) {
                    const donor = teams.slice().sort((a, b) => players.filter((p) => p.teamId === b.id).length - players.filter((p) => p.teamId === a.id).length)[0];
                    players.filter((p) => p.teamId === donor.id).at(-1)!.teamId = empty.id;
                  }
                }
                rebuildTurnOrder();
                render();
              },
            },
          },
          [t(`setup.mode.${m}`)],
        ),
      );

    const playersList = el(
      'div',
      { class: 'grid gap-2' },
      players.map((p, idx) =>
        el('div', { class: 'flex gap-2 items-center' }, [
          el('input', {
            class: 'input flex-1',
            value: p.name,
            placeholder: t('setup.player_name'),
            on: {
              input: (ev) => {
                p.name = (ev.target as HTMLInputElement).value;
              },
            },
          }),
          mode === 'teams'
            ? teamSelect(p)
            : null,
          mode !== 'single' && players.length > 1
            ? el(
                'button',
                {
                  class: 'btn-ghost px-3',
                  on: {
                    click: () => {
                      players.splice(idx, 1);
                      rebuildTurnOrder();
                      render();
                    },
                  },
                },
                ['×'],
              )
            : null,
        ]),
      ),
    );

    const teamsBlock =
      mode === 'teams'
        ? el('section', { class: 'card' }, [
            el('h3', { class: 'font-semibold mb-3' }, [t('setup.teams')]),
            el(
              'div',
              { class: 'grid gap-2 mb-3' },
              teams.map((tt, idx) =>
                el('div', { class: 'flex gap-2 items-center' }, [
                  el('span', {
                    class: 'w-6 h-6 rounded-full',
                    style: `background:${tt.color}`,
                  }),
                  el('input', {
                    class: 'input flex-1',
                    value: tt.name,
                    placeholder: t('setup.team_name'),
                    on: {
                      input: (ev) => {
                        tt.name = (ev.target as HTMLInputElement).value;
                      },
                    },
                  }),
                  teams.length > 2
                    ? el(
                        'button',
                        {
                          class: 'btn-ghost px-3',
                          on: {
                            click: () => {
                              teams.splice(idx, 1);
                              let reassigned = 0;
                              players.forEach((p) => {
                                if (p.teamId === tt.id) p.teamId = teams[reassigned++ % teams.length]?.id;
                              });
                              rebuildTurnOrder();
                              render();
                            },
                          },
                        },
                        ['×'],
                      )
                    : null,
                ]),
              ),
            ),
            el(
              'button',
              {
                class: 'btn-ghost',
                disabled: teams.length >= players.length,
                on: {
                  click: () => {
                    const newTeam = createTeam(`Team ${String.fromCharCode(65 + teams.length)}`, TEAM_COLORS[teams.length % TEAM_COLORS.length]);
                    const donor = teams.slice().sort((a, b) => players.filter((p) => p.teamId === b.id).length - players.filter((p) => p.teamId === a.id).length)[0];
                    const member = players.filter((p) => p.teamId === donor.id).at(-1);
                    teams.push(newTeam); if (member) member.teamId = newTeam.id;
                    rebuildTurnOrder();
                    render();
                  },
                },
              },
              [t('setup.add_team')],
            ),
          ])
        : null;

    const turnOrderBlock = el('section', { class: 'card' }, [
      el('h3', { class: 'font-semibold mb-1' }, [t('setup.turn_order')]),
      el('p', { class: 'text-xs text-slate-400 mb-3' }, [t('setup.turn_order.hint')]),
      el(
        'ol',
        { class: 'grid gap-2' },
        turnOrder.map((e, idx) =>
          el('li', { class: 'flex items-center gap-2 bg-slate-800 rounded-xl p-2' }, [
            el('span', { class: 'w-6 text-center text-slate-400' }, [`${idx + 1}.`]),
            el('span', { class: 'flex-1 font-medium' }, [entityLabel(e)]),
            el(
              'button',
              {
                class: 'btn-ghost px-2 py-1',
                disabled: idx === 0,
                on: { click: () => moveOrder(idx, -1) },
              },
              ['↑'],
            ),
            el(
              'button',
              {
                class: 'btn-ghost px-2 py-1',
                disabled: idx === turnOrder.length - 1,
                on: { click: () => moveOrder(idx, 1) },
              },
              ['↓'],
            ),
          ]),
        ),
      ),
    ]);

    const dailyToggle = game!.metadata.supportsDaily
      ? el('label', { class: 'flex items-center gap-2 text-sm text-slate-300' }, [
          el('input', {
            type: 'checkbox',
            checked: daily,
            on: {
              change: (ev) => {
                daily = (ev.target as HTMLInputElement).checked;
              },
            },
          }),
          t('setup.daily'),
        ])
      : null;

    const errorBox = error
      ? el('div', { class: 'rounded-xl bg-rose-900/40 text-rose-200 p-3 text-sm' }, [error])
      : null;

    mount(
      root,
      el('div', { class: 'grid gap-4' }, [
        el('header', { class: 'flex items-center justify-between' }, [
          el('button', { class: 'btn-ghost', on: { click: () => navigate({ name: 'lobby' }) } }, [
            '← ' + t('common.back'),
          ]),
          el('h1', { class: 'text-xl font-bold flex items-center gap-2' }, [
            game!.metadata.iconUrl
              ? (() => { const img = document.createElement('img'); img.src = assetUrl(game!.metadata.iconUrl); img.alt = ''; img.className = 'w-7 h-7 object-contain shrink-0'; return img; })()
              : el('span', {}, [game!.metadata.icon]),
            document.createTextNode(game!.metadata.name),
          ]),
          el('span', {}, ['']),
        ]),
        el('div', { class: 'theme-divider' }),
        el('section', { class: 'quiz-intro' }, [
          el('strong', {}, [gameId === 'globe' ? copy('Il tuo prossimo giro del mondo.', 'Your next trip around the world.') : copy('Apriamo un’indagine bestiale.', 'Let’s open a wild investigation.')]),
          game!.metadata.description,
          el('p', { class: 'mt-2' }, [copy('Gioca da solo, passa il dispositivo agli amici o sfida due squadre. La sfida del giorno usa lo stesso mistero per tutti, aggiornato a mezzanotte UTC.', 'Play solo, pass the device to friends or challenge two teams. The daily challenge uses the same mystery for everyone, changing at midnight UTC.')]),
        ]),
        el('section', { class: 'card flex flex-wrap gap-2' }, modeButtons),
        mode !== 'single'
          ? el('section', { class: 'card' }, [
              el('h3', { class: 'font-semibold mb-3' }, [t('setup.players')]),
              playersList,
              el(
                'button',
                {
                  class: 'btn-ghost mt-3',
                  disabled: players.length >= game!.metadata.maxPlayers,
                  on: {
                    click: () => {
                      const teamId = mode === 'teams' ? teams[players.length % teams.length]?.id : undefined;
                      players.push(createPlayer(`P${players.length + 1}`, teamId));
                      rebuildTurnOrder();
                      render();
                    },
                  },
                },
                [t('setup.add_player')],
              ),
            ])
          : el('section', { class: 'card' }, [playersList]),
        teamsBlock,
        mode !== 'single' ? turnOrderBlock : null,
        dailyToggle ? el('section', { class: 'card' }, [dailyToggle]) : null,
        errorBox,
        el('button', { class: 'btn-primary w-full', disabled: starting, on: { click: () => void start() } }, [
          starting ? copy('Preparo la partita…', 'Preparing your game…') : t('setup.start'),
        ]),
      ]),
    );
  }

  render();
  (root as HTMLElement & { dispose?: () => void }).dispose = () => { disposed = true; };
  return root;
}
